import React, { memo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { Box } from '@hz-design-system/web-ui';

import { imageFormatter } from '@/client/utils/formatters';
import { Attribute } from '@/client/types/Listings';
import { TImage } from '@/client/types/Images';

import ListingVipLink from '@/client/components/Listings/atoms/ListingVipLink';
import { ListingDescription, ListingFigure, ListingUrgencyBadge } from '@/client/components/ListingsNew/components';

import {
  ListingAttributes,
  ListingExtendedAttributes,
  ListingLocationSellerWebsite,
  ListingPriorityDateFeature,
  ListingTrustIndicators,
  ListingTitlePriceDetailsButton,
} from './components';

import Classes from './ListingListViewContentMweb.module.scss';

type TListingListViewContentMweb = {
  carPassUrl?: Attribute;
  categories?: TCategories;
  categorySpecificDescription?: string;
  correlationId?: string;
  date: string;
  fetchPriority?: 'auto' | 'high' | 'low';
  decodingType?: 'sync' | 'async' | 'auto';
  extendedAttributes?: Attribute[];
  isFavorite?: boolean;
  loadingType?: 'eager' | 'lazy';
  location: { label: string; distance?: string };
  napAvailable?: boolean;
  onFavorite: () => void;
  opvalStickerText?: string;
  pageLocation?: string;
  pictures?: TImage[];
  price: string;
  priorityProduct?: string;
  sellerInformation?: TSellerInformation;
  shouldDisplayExtendedContent?: boolean;
  thinContent?: boolean;
  title: string;
  trackingData?: string;
  trustIndicators?: { value?: string; key: string }[];
  urgencyFeatureActive?: boolean;
  vipUrl: string;
};

const ListingListViewContentMweb = ({
  carPassUrl,
  categories,
  categorySpecificDescription,
  correlationId,
  date,
  decodingType,
  extendedAttributes = [],
  isFavorite = false,
  fetchPriority,
  loadingType,
  location,
  napAvailable = false,
  onFavorite,
  opvalStickerText,
  pageLocation,
  pictures,
  price,
  priorityProduct = '',
  sellerInformation,
  shouldDisplayExtendedContent = false,
  thinContent,
  title,
  trackingData,
  trustIndicators = [],
  urgencyFeatureActive = false,
  vipUrl,
}: TListingListViewContentMweb) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <>
      <ListingVipLink
        vipUrl={vipUrl}
        trackingData={trackingData}
        correlationId={correlationId}
        pageLocation={pageLocation}
        thinContent={thinContent}
        className={Classes['hz-Listing-coverLink']}
      >
        <ListingFigure
          images={imageFormatter(pictures ?? [], title, categories, extendedAttributes)}
          opvalStickerText={opvalStickerText}
          fetchPriority={fetchPriority}
          loadingType={loadingType}
          decodingType={decodingType}
          isFavorite={isFavorite}
          onFavorite={onFavorite}
          mainAspectRatio="square"
          subImagesAspectRatio="landscape"
          showFavoriteIcon={true}
        />
        <Box as="div" name="Listing-group--mweb" className={Classes['hz-Listing-group-mweb']}>
          <ListingTitlePriceDetailsButton
            title={title}
            price={price}
            shouldDisplayExtendedContent={shouldDisplayExtendedContent}
            isDetailsOpen={isDetailsOpen}
            setIsDetailsOpen={setIsDetailsOpen}
          />
          {urgencyFeatureActive && (
            <Box as="div" name="Listing-urgency-badge-mweb">
              <ListingUrgencyBadge />
            </Box>
          )}

          {shouldDisplayExtendedContent && (
            <details
              className={
                isDetailsOpen
                  ? Classes['hz-Listing-extended-content-mweb--open']
                  : Classes['hz-Listing-extended-content-mweb']
              }
              open
            >
              <summary />
              <ListingExtendedAttributes extendedAttributes={extendedAttributes} />
              <ListingDescription.Small description={categorySpecificDescription} color="text-secondary" />
            </details>
          )}

          <Box
            as="div"
            name="Listing-attributes-and-trust-indicators"
            className={Classes['hz-Listing-attributes-and-trust-indicators']}
          >
            <ListingAttributes carPassUrl={carPassUrl} napAvailable={napAvailable} />
            <ListingTrustIndicators trustIndicators={trustIndicators} />
          </Box>
        </Box>
      </ListingVipLink>
      <Box as="div" name="Listing-group-bottom-row-mweb" className={Classes['hz-Listing-group-bottom-row-mweb']}>
        <ListingLocationSellerWebsite location={location} sellerInformation={sellerInformation} />
        <ListingPriorityDateFeature priorityProduct={priorityProduct} date={date} />
      </Box>
    </>
  );
};

// todo: find which properties are really needs to be checked for preventing rerendering
function arePropsEqual(prevProps, nextProps) {
  return isEqual(prevProps, nextProps);
}

export default memo(ListingListViewContentMweb, arePropsEqual);
